/**
 * FinanceReportsView — weekly / monthly / quarterly / annual periodic
 * financial reports with optional AI commentary per row.
 *
 * The Yusuf-as-actor council generates a single-line "what to do this
 * period" recommendation for each entity — cached server-side per
 * (entity_id, cadence, period_label) so it doesn't re-burn LLM tokens
 * on every page load.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Mail, Sparkles, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import Sparkline from '@/components/Sparkline';
import { CADENCES, fmtCurrency } from '@/components/finance/financeUtils';

function ReportTile({ label, value, cur, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${color || 'text-slate-900'}`}>
        {fmtCurrency(value, cur)}
      </div>
    </div>
  );
}

export default function FinanceReportsView({ reportingCurrency, isAdmin }) {
  const [cadence, setCadence] = useState('monthly');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [aiOn, setAiOn] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [commentaryByEntity, setCommentaryByEntity] = useState({});

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/finance/reports/${cadence}`, {
        params: { reporting_currency: reportingCurrency, include_commentary: aiOn ? 1 : 0 },
      });
      setReport(data);
      // Server may return commentary inline on each row; also accept a top-level
      // `commentary_by_entity` map for forward-compat.
      const map = data.commentary_by_entity || {};
      (data.rows || []).forEach((r) => {
        if (r.commentary && !map[r.entity_id]) map[r.entity_id] = r.commentary;
      });
      setCommentaryByEntity(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [cadence, reportingCurrency, aiOn]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/finance/reports-history');
      setHistory(data.history || []);
    } catch { /* ignore — only admin sees this */ }
  }, []);

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effects */
  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => { if (isAdmin) loadHistory(); }, [loadHistory, isAdmin]);
  /* eslint-enable */

  const sendNow = async () => {
    if (!window.confirm(`Send this ${cadence} report to every holding admin / CEO / CFO right now?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/finance/reports/${cadence}/send-now`);
      if (data.skipped_no_smtp) {
        toast.warning('SMTP not configured — report built but email dispatch skipped.');
      } else {
        toast.success(`Sent to ${data.recipients} recipient${data.recipients === 1 ? '' : 's'} across ${data.tenants} tenant${data.tenants === 1 ? '' : 's'}.`);
      }
      await loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  // Force-regenerate commentary (bypasses the server-side cache).
  const refreshCommentary = async () => {
    if (!report) return;
    setAiGenerating(true);
    try {
      const { data } = await api.post(`/finance/reports/${cadence}/commentary/regenerate`, {
        reporting_currency: reportingCurrency,
      });
      const map = {};
      (data.commentary || []).forEach((c) => { map[c.entity_id] = c.text; });
      setCommentaryByEntity(map);
      toast.success(`AI commentary refreshed for ${data.commentary?.length || 0} entit${(data.commentary?.length || 0) === 1 ? 'y' : 'ies'}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI commentary generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <div data-testid="finance-reports-view">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {CADENCES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCadence(c.key)}
              data-testid={`finance-cadence-${c.key}`}
              className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                cadence === c.key
                  ? 'bg-[#0F1A2D] text-white border-[#0F1A2D]'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="font-semibold">{c.label}</div>
              <div className={`text-[10px] ${cadence === c.key ? 'opacity-70' : 'text-slate-400'}`}>{c.sub}</div>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* AI commentary toggle */}
          <button
            onClick={() => setAiOn((x) => !x)}
            data-testid="finance-ai-toggle"
            className={`px-3 py-2 text-sm rounded-lg border flex items-center gap-1.5 transition-colors ${
              aiOn
                ? 'bg-[#FFF8E5] text-[#0F1A2D] border-[#8B3FC5]'
                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
            }`}
            title="Toggle Yusuf-as-actor AI commentary on each entity row"
          >
            <Sparkles className="w-4 h-4" />
            <span className="font-semibold">AI commentary {aiOn ? 'on' : 'off'}</span>
          </button>
          {aiOn && isAdmin && (
            <button
              onClick={refreshCommentary}
              disabled={aiGenerating}
              data-testid="finance-ai-refresh"
              className="px-2 py-2 text-xs border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Bypass cache and regenerate"
            >
              <RefreshCw className={`w-4 h-4 ${aiGenerating ? 'animate-spin' : ''}`} />
            </button>
          )}
          {isAdmin && (
            <button
              onClick={sendNow}
              disabled={sending}
              data-testid="finance-report-send-now"
              className="px-3 py-2 text-sm border border-[#8B3FC5] text-[#0F1A2D] bg-[#FFF8E5] rounded-lg hover:bg-[#FBE9A4] flex items-center gap-1.5 disabled:opacity-50"
            >
              <Mail className="w-4 h-4" /> {sending ? 'Sending…' : 'Send report now'}
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-sm text-slate-500 py-12 text-center">Loading report…</div>
      )}

      {!loading && report && (
        <div className="space-y-4">
          {/* Window banner */}
          {report.window && (
            <div className="bg-gradient-to-r from-[#8B3FC5] to-[#B7941F] text-white rounded-2xl p-5 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider opacity-80">{report.cadence} report</div>
                <div className="text-2xl font-bold mt-1" data-testid="finance-report-label">{report.window.label}</div>
                <div className="text-xs opacity-80 mt-1">
                  Amounts in <strong>{report.reporting_currency}</strong>
                  {report.fx_normalised ? ' · FX-normalised across portfolio' : ' · FX rates unavailable, mixed-currency sum'}
                </div>
              </div>
              <FileText className="w-10 h-10 opacity-30" />
            </div>
          )}

          {/* Totals strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="finance-report-totals">
            <ReportTile label="End cash" value={report.totals.total_end_cash} cur={report.reporting_currency} />
            <ReportTile label="Revenue" value={report.totals.total_revenue} cur={report.reporting_currency} color="text-emerald-700" />
            <ReportTile label="Expenses" value={report.totals.total_expenses} cur={report.reporting_currency} color="text-rose-700" />
            <ReportTile label="Net burn" value={report.totals.total_burn} cur={report.reporting_currency} color={report.totals.total_burn > 0 ? 'text-rose-700' : 'text-emerald-700'} />
            <ReportTile label="New funding" value={report.totals.total_new_funding} cur={report.reporting_currency} />
          </div>

          {/* Per-entity table */}
          {report.rows.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500" data-testid="finance-report-empty">
              No financial activity recorded for this period.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm" data-testid="finance-report-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="py-3 px-4">Entity</th>
                    <th className="py-3 px-4 text-right">End cash</th>
                    <th className="py-3 px-4 text-right">Revenue</th>
                    <th className="py-3 px-4 text-right">Burn</th>
                    <th className="py-3 px-4 text-right">Δ Revenue</th>
                    <th className="py-3 px-4 text-right">Trend</th>
                    <th className="py-3 px-4 text-right">New funding</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => {
                    const ai = aiOn ? (commentaryByEntity[r.entity_id] || r.commentary) : null;
                    return (
                      <React.Fragment key={r.entity_id}>
                        {/* Entity row — drops its border when an AI commentary
                            row follows so the pair reads as one group, with
                            a thicker `border-b-2` underneath the commentary
                            cell separating it from the NEXT entity group. */}
                        <tr
                          className={ai ? 'border-b-0' : 'border-b border-slate-100'}
                          data-testid={`finance-report-row-${r.entity_id}`}
                        >
                          <td className="py-3 px-4">
                            <div className="font-semibold text-slate-900">{r.entity_name}</div>
                            <div className="text-[10px] text-slate-400">{r.currency} → {report.reporting_currency} · {r.period}</div>
                          </td>
                          <td className="py-3 px-4 text-right tabular-nums">{fmtCurrency(r.end_cash, report.reporting_currency)}</td>
                          <td className="py-3 px-4 text-right tabular-nums text-emerald-700">{fmtCurrency(r.revenue, report.reporting_currency)}</td>
                          <td className="py-3 px-4 text-right tabular-nums text-rose-700">{fmtCurrency(r.burn, report.reporting_currency)}</td>
                          <td className="py-3 px-4 text-right tabular-nums">
                            {r.revenue_delta_pct === null || r.revenue_delta_pct === undefined ? (
                              <span className="text-slate-400">—</span>
                            ) : (
                              <span className={r.revenue_delta_pct >= 0 ? 'text-emerald-700' : 'text-rose-700'}>
                                {r.revenue_delta_pct >= 0 ? '+' : ''}{r.revenue_delta_pct}%
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {r.history && r.history.length >= 2 ? (
                              <Sparkline
                                data={r.history.map((h) => ({ label: h.period, value: h.end_cash }))}
                                height={28}
                                accent="#8B3FC5"
                                formatValue={(v) => fmtCurrency(v, report.reporting_currency)}
                                testId={`finance-report-trend-${r.entity_id}`}
                              />
                            ) : (
                              <span className="text-slate-400 text-[10px]">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right tabular-nums">{fmtCurrency(r.new_funding, report.reporting_currency)}</td>
                        </tr>
                        {ai && (
                          <tr
                            className="bg-[#FFFBEC]/60 border-b-2 border-slate-200"
                            data-testid={`finance-report-commentary-${r.entity_id}`}
                          >
                            <td colSpan={7} className="py-2.5 px-4 border-t border-[#8B3FC5]/30">
                              <div className="flex items-start gap-2 text-[13px] text-slate-700">
                                <Sparkles className="w-3.5 h-3.5 mt-0.5 text-[#5E1F8B] shrink-0" />
                                <span className="italic">{ai}</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Dispatch history */}
          {isAdmin && history.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Recently sent</div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {history.slice(0, 12).map((h, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium capitalize">{h.cadence}</span>
                      <span className="text-slate-900">{h.label}</span>
                    </div>
                    <div className="text-slate-500">
                      {h.recipients?.length || 0} recipient{(h.recipients?.length || 0) === 1 ? '' : 's'} · {new Date(h.sent_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-400 text-center pt-2">
            Reports are scheduled to email automatically: weekly Mon 06:00 UTC · monthly 1st of month · quarterly Jan/Apr/Jul/Oct 1st · annually Jan 1st. Daily emails are disabled per Amwāl OS spec.
          </div>
        </div>
      )}
    </div>
  );
}
